import React, { useMemo } from 'react';
import { Row, Col, Typography, Radio, Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { FormValue } from '../useForm';
import useProducts from 'src/queries/useProducts';
import { productFormat } from 'src/utils/product';
import {
  getProductName,
  isImportedProductOnly,
  normalizeParenthesis,
} from 'src/utils/helper';
import { BLANK_OPTION } from 'src/constants/app';

type Props = {
  index: number;
  formInfo: UseFormReturn<FormValue, any>;
  dataReEstimates?: Response.QuotationItem[];
  machiningMethod: string;
  dataReEstimate?: Response.QuotationItem;
  draftVariety: string;
  draftShape: string;
  draftProduct: string;
  setDraftVariety: (item: string) => void;
  setDraftShape: (item: string) => void;
  setDataReEstimate: (data: Response.QuotationItem[]) => void;
  resetInputData: (index: number) => void;
  onClose: () => void;
};

type SelectType = 'SELECT' | 'RADIO';

const ProductList: React.FC<Props> = ({
  formInfo,
  index,
  dataReEstimates = [],
  machiningMethod,
  dataReEstimate,
  draftVariety,
  draftShape,
  draftProduct,
  setDataReEstimate,
  resetInputData,
  onClose,
}) => {
  const { t } = useTranslation();
  const { setValue, clearErrors } = formInfo;

  const { products: listProduct } = useProducts({
    hinshL3Cd: draftVariety,
    keijoL3Cd: draftShape,
  });

  const handleProductSelection = (selected, type: SelectType) => {
    const selectedProductId =
      type === 'SELECT' ? selected.value : selected.target?.value;

    const selectedProduct =
      listProduct?.find((item) => item.productId === selectedProductId) || null;

    const productName =
      type === 'RADIO'
        ? getProductName(selectedProduct, true)
        : selected.option?.label;

    setProductItem(selectedProduct, productName);

    if (selectedProduct) {
      setValue(`priceQuotes.${index}.variety`, selectedProduct.hinshL3Cd);
      setValue(`priceQuotes.${index}.shape`, selectedProduct.keijoL3Cd);
    }

    setValue(`priceQuotes.${index}.machiningItem`, {
      value: '',
      label: '',
    });
    clearErrors(`priceQuotes.${index}.machiningItem`);
    resetInputData(index);
    setValue(`priceQuotes.${index}.isResetByProductSelection`, true);
    onClose();
  };

  const setProductItem = (selectedProduct, productName) => {
    const productId = selectedProduct?.productId;
    const varietyId = selectedProduct?.hinshL3Cd;
    const shapeId = selectedProduct?.keijoL3Cd;

    const productItem = listProduct?.find(
      (item) =>
        productFormat.mergeProductId(
          item.hakudoShhnCd,
          item.shhnCd,
          item.api1ShhnCd,
          item.api2ShhnCd,
        ) == productId,
    );
    setValue(`priceQuotes.${index}.productItem`, productItem);
    setValue(`priceQuotes.${index}.product`, productId);
    const importedOnly =
      productId &&
      isImportedProductOnly({
        product: productId,
        checkZ0: true,
        method: machiningMethod,
      });
    setValue(`priceQuotes.${index}.isImportedProductOnlyAndZ0`, importedOnly);

    const indexItem = dataReEstimates.findIndex(
      (item) => item.id === dataReEstimate?.id,
    );

    dataReEstimates[indexItem] = {
      ...dataReEstimates[indexItem],
      hinshL3Cd: varietyId,
      keijoL3Cd: shapeId,
      product: productId,
      hakudoShhnm: productName,
      setdnKakoCd: '',
      setdnkako: '',
      inzu: '',
      biko: '',
      optc091: '',
    };
    setDataReEstimate([...dataReEstimates]);
  };

  const productOptions = useMemo(
    () =>
      Array.isArray(listProduct)
        ? listProduct.map((item) => ({
            label: getProductName(item, true),
            value: item.productId,
          }))
        : [],
    [listProduct?.length, JSON.stringify(listProduct)],
  );

  return (
    <>
      <Row>
        <Col span={12}>
          <Row className="category_block_infor_row variety_row">
            <Col
              xs={24}
              sm={24}
              md={5}
              lg={6}
              xl={6}
              className="category-label"
            >
              <Typography.Text className="text_category_label">
                {t('pricing_page.product_field')}
              </Typography.Text>
            </Col>
            <Col xs={20} sm={18} md={19} lg={14} xl={14}>
              <Select
                showSearch
                onChange={(value, option) =>
                  handleProductSelection({ value, option }, 'SELECT')
                }
                options={[BLANK_OPTION, ...productOptions]}
                value={draftProduct}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mt-14">
        <Typography.Text className="font_semibold">
          {t('product.items_name')}
        </Typography.Text>
      </Row>

      <Row>
        <Col xs={24} sm={24} md={24} xl={24} className="products-wrap">
          <Radio.Group
            className="w-full product_checkbox"
            onChange={(e) => handleProductSelection(e, 'RADIO')}
            value={draftProduct}
          >
            {listProduct?.map((item, index) => {
              return (
                <Radio key={index} className="mr-0" value={item.productId}>
                  <Tooltip
                    title={normalizeParenthesis(getProductName(item, true))}
                    placement="top"
                    overlayClassName="custom-tooltip"
                  >
                    <Typography.Text
                      ellipsis
                      style={{
                        color: item.productId === draftProduct ? 'red' : '#000',
                      }}
                    >
                      <div className="product-name_label">
                        <span className="multi_line_ellipsis">
                          {normalizeParenthesis(getProductName(item, true))}
                        </span>
                      </div>
                    </Typography.Text>
                  </Tooltip>
                </Radio>
              );
            })}
          </Radio.Group>
        </Col>
      </Row>
    </>
  );
};

export default ProductList;
